import { useEffect, useState } from 'react';
import { useVoiceDictation, VOICE_MAX_SECONDS, type VoiceResult } from '@/hooks/useVoiceDictation';
import { loadVoiceConfig, type VoiceConfig } from '@/lib/voiceConfig';

/**
 * VoiceInputButton — bouton microphone de dictée vocale (Whisper).
 *
 * Se masque automatiquement si le Super Admin n'a pas activé la fonctionnalité,
 * et n'apparaît que pour les utilisateurs connectés. L'utilisateur parle, le
 * texte transcrit est inséré dans le champ via `onTranscript` — jamais d'envoi
 * automatique.
 *
 * Composant réutilisable sur tous les champs de texte utiles (chat, notes
 * internes, description de produit, formulaire de contact…).
 */

interface VoiceInputButtonProps {
  userId?: number;
  /** Reçoit le texte transcrit (champs « classiques » : insertion dans le champ). */
  onTranscript?: (text: string) => void;
  /**
   * Mode « note vocale » (chat IA) : reçoit le texte ET l'audio. Le texte part
   * à l'IA, l'audio s'affiche comme un message vocal réécoutable.
   */
  onVoiceResult?: (result: VoiceResult) => void;
  /** Libellé d'accessibilité (ex. « Dicter la description »). */
  title?: string;
  /** Position de la pastille d'erreur : au-dessus ou en dessous du bouton. */
  placement?: 'above' | 'below';
}

const LANGUAGES = [
  { code: 'auto', label: 'Auto' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'darija', label: 'Darija' },
  { code: 'es', label: 'Español' },
];

function mmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VoiceInputButton({
  userId,
  onTranscript,
  onVoiceResult,
  title = 'Dicter un message',
  placement = 'above',
}: VoiceInputButtonProps) {
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [language, setLanguage] = useState('auto');

  // Lecture de la configuration plateforme (activation gérée par le Super Admin).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    loadVoiceConfig().then((cfg) => {
      if (cancelled) return;
      setConfig(cfg);
      setLanguage(cfg.language);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const { status, seconds, error, start, stop, cancel, reset } = useVoiceDictation({
    userId,
    language,
    onTranscript,
    onResult: onVoiceResult,
  });

  if (!userId || !config || !config.enabled) return null;

  const errorPosition = placement === 'below'
    ? 'absolute right-0 top-full mt-2'
    : 'absolute right-0 bottom-full mb-2';

  return (
    <div className="relative flex items-center gap-1.5 flex-shrink-0">
      {status === 'error' && (
        <div className={`${errorPosition} w-64 bg-background-50 border border-background-200/70 rounded-lg p-3 z-50`}>
          <p className="text-xs text-red-600 flex items-start gap-1.5 mb-2">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <span>{error}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { reset(); void start(); }}
              className="flex-1 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={reset}
              className="px-3 py-1.5 rounded-full border border-background-200/70 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {status === 'idle' && (
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          title="Langue de la dictée"
          aria-label="Langue de la dictée"
          className="h-10 max-w-[92px] rounded-full border border-background-200/70 bg-background-50 px-2 text-xs text-foreground-600 cursor-pointer focus:outline-none focus:border-accent-300"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      )}

      {status === 'idle' && (
        <button
          type="button"
          onClick={start}
          title={title}
          aria-label={title}
          className="w-10 h-10 rounded-full border border-background-200/70 bg-background-50 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-100 hover:border-accent-300 transition-colors flex-shrink-0"
        >
          <i className="ri-mic-line text-lg"></i>
        </button>
      )}

      {status === 'requesting' && (
        <div className="w-10 h-10 rounded-full border border-background-200/70 bg-background-50 flex items-center justify-center flex-shrink-0">
          <i className="ri-loader-4-line animate-spin text-foreground-500"></i>
        </div>
      )}

      {status === 'recording' && (
        <>
          <span className="flex items-center gap-1.5 h-10 px-3 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-semibold whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            {mmss(seconds)} / {mmss(VOICE_MAX_SECONDS)}
          </span>
          <button
            type="button"
            onClick={cancel}
            title="Annuler"
            aria-label="Annuler l'enregistrement"
            className="w-9 h-9 rounded-full border border-background-200/70 bg-background-50 text-foreground-500 flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors flex-shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
          <button
            type="button"
            onClick={stop}
            title="Arrêter et transcrire"
            aria-label="Arrêter et transcrire"
            className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors flex-shrink-0"
          >
            <i className="ri-stop-fill"></i>
          </button>
        </>
      )}

      {status === 'transcribing' && (
        <span className="flex items-center gap-1.5 h-10 px-3 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap">
          <i className="ri-loader-4-line animate-spin"></i>
          Transcription…
        </span>
      )}
    </div>
  );
}